import "./FormBuilderPage.scss";

import { Badge, Col, Form, Modal, Row } from "react-bootstrap";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppInput from "../../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import CompanyAPI from "../../../../company/CompanyAPI";
import FormTemplateAPI from "../../api/FormTemplateAPI";
import HallAppToast from "../../../../../commons/HallAppToast";
import ReportHeader from "../../../../report-full/components/ReportHeader";
import { useParams } from "react-router-dom";

const formAPI = new FormTemplateAPI();
const companyAPI = new CompanyAPI();

// ===================== consts =====================
const FIELD_TYPES = [
  { value: "TEXT", label: "Texto curto" },
  { value: "TEXTAREA", label: "Texto longo" },
  { value: "BOOLEAN", label: "Sim/Não" },
  { value: "SELECT", label: "Lista (única escolha)" },
  { value: "CHECKBOX", label: "Lista (múltipla escolha)" },
  { value: "NUMBER", label: "Número" },
  { value: "DATE", label: "Data" },

  // ✅ novos
  { value: "IMAGE_SELECT", label: "Alternativas com imagem" },
  { value: "IMAGE_UPLOAD", label: "Upload de imagem" },
];

// SELECT / CHECKBOX / IMAGE_SELECT precisam de “opções”
const OPTIONS_TYPES = new Set(["SELECT", "CHECKBOX", "IMAGE_SELECT"]);

const uid = () => Math.random().toString(16).slice(2) + Date.now().toString(16);

const defaultField = () => ({
  id: uid(),
  label: "",
  type: "TEXT",
  required: false,
  helper: "",

  // SELECT/CHECKBOX
  options: ["Opção 1", "Opção 2"],

  // IMAGE_SELECT
  // imageUrl = url persistida (backend/S3)
  // previewUrl = ObjectURL (somente quando escolhe file)
  imageOptions: [{ text: "Opção 1", file: null, previewUrl: "", imageUrl: "" }],

  sortOrder: null,
  status: "ACTIVE",
  configJson: null,
});

// parse seguro do configJson do backend
const safeJson = (s) => {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

// ============== helpers de ObjectURL (sem vazamento) ==============
const revokePreview = (url) => {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch (_) {}
};

const revokeAllPreviewsFromField = (field) => {
  if (!field) return;
  if (field.type !== "IMAGE_SELECT") return;
  if (!Array.isArray(field.imageOptions)) return;

  field.imageOptions.forEach((opt) => {
    if (opt?.previewUrl) revokePreview(opt.previewUrl);
  });
};

// ===================== EditorContent (FORA) =====================
function EditorContent({ field, updateField, removeField, onDone }) {
  if (!field) return null;

  const needsOptions = OPTIONS_TYPES.has(field.type);
  const isImageSelect = field.type === "IMAGE_SELECT";
  const isImageUpload = field.type === "IMAGE_UPLOAD";

  const handleTypeChange = (newType) => {
    // ✅ se estava em IMAGE_SELECT, revoga previews antes de trocar o tipo
    if (field.type === "IMAGE_SELECT") {
      revokeAllPreviewsFromField(field);
    }

    const patch = { type: newType };

    if (newType === "IMAGE_SELECT") {
      patch.imageOptions =
        Array.isArray(field.imageOptions) && field.imageOptions.length
          ? field.imageOptions
          : [{ text: "Opção 1", file: null, previewUrl: "", imageUrl: "" }];

      patch.options = undefined;
      patch.configJson = null;
    } else if (newType === "IMAGE_UPLOAD") {
      patch.options = undefined;
      patch.imageOptions = undefined;
      patch.configJson = null; // gerado no save (padrão)
    } else if (OPTIONS_TYPES.has(newType)) {
      // SELECT/CHECKBOX
      patch.options = field.options?.length ? field.options : ["Opção 1"];
      patch.imageOptions = undefined;
      patch.configJson = null;
    } else {
      patch.options = undefined;
      patch.imageOptions = undefined;
      patch.configJson = null;
    }

    updateField(field.id, patch);
  };

  // ============ OPTIONS (SELECT / CHECKBOX) ============
  const options = field.options || [];

  const setOptionAt = (i, v) => {
    const next = [...options];
    next[i] = v;
    updateField(field.id, { options: next });
  };

  const addOption = () => {
    updateField(field.id, { options: [...options, `Opção ${options.length + 1}`] });
  };

  const removeOptionAt = (i) => {
    const next = options.filter((_, idx) => idx !== i);
    updateField(field.id, { options: next.length ? next : ["Opção 1"] });
  };

  // ============ IMAGE OPTIONS (IMAGE_SELECT) ============
  const imageOptions = Array.isArray(field.imageOptions) ? field.imageOptions : [];

  const setImageOptionAt = (i, patch) => {
    const next = imageOptions.map((x, idx) => {
      if (idx !== i) return x;

      // ✅ se trocar arquivo, revoga preview anterior
      if (patch?.file && x?.previewUrl) revokePreview(x.previewUrl);

      return { ...x, ...patch };
    });

    updateField(field.id, { imageOptions: next });
  };

  const addImageOption = () => {
    updateField(field.id, {
      imageOptions: [
        ...imageOptions,
        { text: `Opção ${imageOptions.length + 1}`, file: null, previewUrl: "", imageUrl: "" },
      ],
    });
  };

  const removeImageOptionAt = (i) => {
    const target = imageOptions[i];
    if (target?.previewUrl) revokePreview(target.previewUrl);

    const next = imageOptions.filter((_, idx) => idx !== i);
    updateField(field.id, {
      imageOptions: next.length ? next : [{ text: "Opção 1", file: null, previewUrl: "", imageUrl: "" }],
    });
  };

  const onPickImage = (idx, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    // ✅ não apaga imageUrl (se existia), só coloca file + preview
    setImageOptionAt(idx, { file, previewUrl });
  };

  // ✅ desfaz troca: remove somente o novo file/preview, mantém a imageUrl do backend
  const undoPickedImage = (idx) => {
    const current = imageOptions[idx];
    if (current?.previewUrl) revokePreview(current.previewUrl);
    setImageOptionAt(idx, { file: null, previewUrl: "" });
  };

  // AppInput pede errors/touched (formik). Aqui a gente passa vazio.
  const errors = {};
  const touched = {};

  return (
    <div className="fb-editor">
      <div className="fb-section">
        <div className="fb-section__title">Configuração do campo</div>

        <Row className="g-2">
          <Col xs={12}>
            <AppInput
              errors={errors}
              touched={touched}
              name="label"
              value={field.label || ""}
              onChange={(e) => updateField(field.id, { label: e.target.value })}
              type="text"
              label="Pergunta (label)"
              isRequired
              placeholder="Ex: Você possui alergias?"
            />
          </Col>

          <Col xs={12} sm={6}>
            <div className="fb-rb-input">
              <Form.Label className="fb-rb-label">Tipo do campo</Form.Label>
              <Form.Select className="fb-select" value={field.type} onChange={(e) => handleTypeChange(e.target.value)}>
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>

          <Col xs={12} sm={6}>
            <div className="fb-rb-input">
              <Form.Label className="fb-rb-label">Obrigatório</Form.Label>
              <div className="fb-switch">
                <Form.Check
                  type="switch"
                  checked={!!field.required}
                  onChange={(e) => updateField(field.id, { required: e.target.checked })}
                  label={field.required ? "Sim" : "Não"}
                />
              </div>
            </div>
          </Col>

          <Col xs={12}>
            <AppInput
              errors={errors}
              touched={touched}
              name="helper"
              value={field.helper || ""}
              onChange={(e) => updateField(field.id, { helper: e.target.value })}
              type="text"
              label="Texto de ajuda (opcional)"
              placeholder="Ex: Ex.: progressiva, coloração..."
            />
          </Col>
        </Row>
      </div>

      {/* ✅ Opções para SELECT/CHECKBOX (layout igual) */}
      {needsOptions && !isImageSelect && (
        <div className="fb-section">
          <div className="fb-section__title">Opções</div>

          <div className="fb-options">
            {options.map((opt, idx) => (
              <div className="fb-option" key={`${field.id}-opt-${idx}`}>
                <Form.Control
                  className="fb-option__input"
                  value={opt}
                  onChange={(e) => setOptionAt(idx, e.target.value)}
                  placeholder={`Opção ${idx + 1}`}
                />

                <button type="button" className="fb-text-btn fb-text-btn--danger" onClick={() => removeOptionAt(idx)}>
                  Remover
                </button>
              </div>
            ))}
          </div>

          <div className="fb-actions-row">
            <AppLinkButton name="Adicionar opção" fullWidth={true} onClick={addOption} />
          </div>
        </div>
      )}

      {/* ✅ IMAGE_SELECT: mostra url do backend; ao editar, mostra preview do novo file */}
      {isImageSelect && (
        <div className="fb-section">
          <div className="fb-section__title">Opções (imagem + texto)</div>

          <div className="fb-options">
            {imageOptions.map((opt, idx) => {
              const displayUrl = opt?.previewUrl || opt?.imageUrl || "";

              return (
                <div className="fb-option" key={`${field.id}-imgopt-${idx}`}>
                  <Form.Control
                    className="fb-option__input"
                    value={opt.text || ""}
                    onChange={(e) => setImageOptionAt(idx, { text: e.target.value })}
                    placeholder={`Texto da opção ${idx + 1}`}
                  />

                  <Form.Control
                    className="fb-option__input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => onPickImage(idx, e.target.files?.[0] || null)}
                  />

                  {displayUrl ? (
                    <div style={{ width: "100%", marginTop: 8 }}>
                      <img
                        src={displayUrl}
                        alt="preview"
                        style={{ width: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 12 }}
                      />
                    </div>
                  ) : null}

                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    {/* ✅ aparece somente quando o usuário escolheu novo arquivo */}
                    {opt?.previewUrl ? (
                      <button type="button" className="fb-text-btn" onClick={() => undoPickedImage(idx)}>
                        Desfazer troca
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="fb-text-btn fb-text-btn--danger"
                      onClick={() => removeImageOptionAt(idx)}
                    >
                      Remover
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="fb-actions-row">
            <AppLinkButton name="Adicionar opção" fullWidth={true} onClick={addImageOption} />
          </div>

          <div className="fb-tip" style={{ marginTop: 10 }}>
            Se a opção já tiver imagem salva (URL), ela aparece aqui. Se você escolher um novo arquivo, aparece o preview
            local. No save, os arquivos seguem em multipart e o backend substitui/gera a nova URL.
          </div>
        </div>
      )}

      {/* ✅ IMAGE_UPLOAD: sem inputs extras (padrão image/* e 5MB) */}
      {isImageUpload && (
        <div className="fb-section">
          <div className="fb-section__title">Upload de imagem</div>
          <div className="fb-tip">Campo de upload de imagem no formulário final. Padrão: image/* e limite 5MB.</div>
        </div>
      )}

      <div className="fb-actions">
        <button type="button" className="fb-text-btn fb-text-btn--danger" onClick={() => removeField(field.id)}>
          Remover campo
        </button>

        <div className="fb-actions__right">
          <AppLinkButton name="Concluir" fullWidth={false} onClick={onDone} />
        </div>
      </div>
    </div>
  );
}

// ===================== Page =====================
export default function FormBuilderPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [formModel, setFormModel] = useState({
    id: null,
    name: "Anamnese",
    description: "Formulário de atendimento",
    active: true,
    templateType: "OUTRO",
    status: "ACTIVE",
  });

  const [fields, setFields] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const [editingFieldId, setEditingFieldId] = useState(null);
  const [showMobileEditor, setShowMobileEditor] = useState(false);

  const editingField = useMemo(
    () => fields.find((f) => f.id === editingFieldId) || null,
    [fields, editingFieldId]
  );

  const isMobile = () => window.matchMedia && window.matchMedia("(max-width: 991px)").matches;

  const openEditor = useCallback((fid) => {
    setEditingFieldId(fid);
    if (isMobile()) setShowMobileEditor(true);
  }, []);

  const closeEditor = useCallback(() => {
    setShowMobileEditor(false);
    setEditingFieldId(null);
  }, []);

  // ✅ cleanup: revoga previews ao sair da página (evita leak)
  useEffect(() => {
    return () => {
      (fields || []).forEach((f) => revokeAllPreviewsFromField(f));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      if (isNew) {
        setFormModel((p) => ({
          ...p,
          id: null,
          name: "Novo Formulário",
          description: "",
          active: true,
          templateType: "OUTRO",
          status: "ACTIVE",
        }));
        setFields([]);
        return;
      }

      try {
        setLoading(true);
        const companyId = companyAPI.getCompanyManagment().id;

        const res = await formAPI.getById(id, companyId);
        const data = res?.data || res;

        setFormModel({
          id: data?.id ?? null,
          name: data?.name ?? "",
          description: data?.description ?? "",
          active: data?.active ?? true,
          templateType: data?.templateType ?? "OUTRO",
          status: data?.status ?? (data?.active ? "ACTIVE" : "INACTIVE"),
        });

        const mapped = (data?.fields || []).map((f, idx) => {
          const conf = safeJson(f?.configJson);
          const fieldType = f?.fieldType ?? "TEXT";

          const imageOptions =
            fieldType === "IMAGE_SELECT" && Array.isArray(conf?.imageOptions)
              ? conf.imageOptions.map((x) => ({
                  text: x?.text ?? "",
                  file: null,
                  previewUrl: "", // ✅ preview é só para file novo
                  imageUrl: x?.imageUrl ?? "", // ✅ url persistida
                }))
              : undefined;

          return {
            id: f?.id ?? uid(),
            label: f?.label ?? "",
            type: fieldType,
            required: !!f?.required,
            helper: f?.helperText ?? "",
            sortOrder: f?.sortOrder ?? idx + 1,
            status: f?.status ?? "ACTIVE",
            configJson: f?.configJson ?? null,

            options: Array.isArray(conf?.options) ? conf.options : undefined,
            imageOptions,
          };
        });

        mapped.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setFields(mapped);
      } catch (_) {
        HallAppToast.genericError();
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateField = useCallback((fid, patch) => {
    setFields((prev) => prev.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
  }, []);

  const addField = useCallback((type = "TEXT") => {
    const f = defaultField();
    f.type = type;
    f.label = FIELD_TYPES.find((t) => t.value === type)?.label || "Novo campo";

    if (!OPTIONS_TYPES.has(type)) f.options = undefined;
    if (type !== "IMAGE_SELECT") f.imageOptions = undefined;

    setFields((prev) => {
      const next = [...prev, { ...f, sortOrder: prev.length + 1 }];
      return next;
    });

    setEditingFieldId(f.id);
    if (isMobile()) setShowMobileEditor(true);
  }, []);

  const removeField = useCallback(
    (fid) => {
      setFields((prev) => {
        const target = prev.find((x) => x.id === fid);
        // ✅ revoga previews desse campo antes de remover
        revokeAllPreviewsFromField(target);

        return prev.filter((f) => f.id !== fid);
      });

      if (editingFieldId === fid) {
        setShowMobileEditor(false);
        setEditingFieldId(null);
      }
    },
    [editingFieldId]
  );

  const duplicateField = useCallback((fid) => {
    setFields((prev) => {
      const original = prev.find((x) => x.id === fid);
      if (!original) return prev;

      // ✅ duplicar IMAGE_SELECT: não duplicar "file" nem "previewUrl"
      const copiedImageOptions =
        original.type === "IMAGE_SELECT" && Array.isArray(original.imageOptions)
          ? original.imageOptions.map((x) => ({
              text: x?.text ?? "",
              file: null,
              previewUrl: "",
              imageUrl: x?.imageUrl ?? "",
            }))
          : original.imageOptions;

      const copy = {
        ...original,
        id: uid(),
        label: `${original.label} (cópia)`,
        sortOrder: prev.length + 1,
        imageOptions: copiedImageOptions,
      };

      const idx = prev.findIndex((x) => x.id === fid);
      const next = [...prev];
      next.splice(idx + 1, 0, copy);

      return next.map((f, i) => ({ ...f, sortOrder: i + 1 }));
    });
  }, []);

  const moveField = useCallback((fid, dir) => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === fid);
      if (idx < 0) return prev;

      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;

      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(nextIdx, 0, item);

      return next.map((f, i) => ({ ...f, sortOrder: i + 1 }));
    });
  }, []);

  function buildSaveFormData({ payload, fields: allFields }) {
    const fd = new FormData();

    // ✅ payload como application/json
    fd.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }));

    const filesMeta = [];
    let fileIndex = 0;

    (allFields || []).forEach((f) => {
      const fieldType = f?.type || f?.fieldType;

      if (fieldType === "IMAGE_SELECT") {
        (f.imageOptions || []).forEach((opt, optIndex) => {
          if (!opt?.file) return;

          fd.append("files", opt.file, opt.file.name);

          filesMeta.push({
            fieldId: typeof f?.id === "number" ? f.id : null,
            fieldTempId: typeof f?.id === "string" ? f.id : null,
            optIndex,
            fileIndex,
            originalName: opt.file.name,
            contentType: opt.file.type,
          });

          fileIndex += 1;
        });
      }
    });

    // ✅ filesMeta como application/json
    fd.append("filesMeta", new Blob([JSON.stringify(filesMeta)], { type: "application/json" }));

    return fd;
  }

  const saveForm = useCallback(async () => {
    try {
      setLoading(true);

      const companyId = companyAPI.getCompanyManagment().id;

      const payload = {
        id: formModel?.id || null,
        companyId,
        name: String(formModel?.name || "").trim(),
        description: String(formModel?.description || "").trim(),
        templateType: formModel?.templateType || "OUTRO",
        active: formModel?.active ?? true,
        status: formModel?.status || (formModel?.active ? "ACTIVE" : "INACTIVE"),
        fields: (fields || [])
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((f, idx) => {
            const fieldType = f?.type || f?.fieldType || "TEXT";
            const isSelectOrCheckbox = fieldType === "SELECT" || fieldType === "CHECKBOX";
            const isImageSelect = fieldType === "IMAGE_SELECT";
            const isImageUpload = fieldType === "IMAGE_UPLOAD";

            let configJson = null;

            if (isSelectOrCheckbox) {
              const options = (f?.options || [])
                .filter(Boolean)
                .map((x) => String(x).trim())
                .filter(Boolean);

              configJson = JSON.stringify({ options: options.length ? options : ["Opção 1"] });
            }

            if (isImageSelect) {
              const imageOptions = Array.isArray(f?.imageOptions) ? f.imageOptions : [];
              const normalized = imageOptions.map((x) => ({
                text: String(x?.text || "").trim(),
                // ✅ preserva URL antiga (backend já tem)
                imageUrl: String(x?.imageUrl || "").trim() || null,
                // ✅ indica se veio arquivo novo (troca)
                hasFile: !!x?.file,
              }));

              configJson = JSON.stringify({ imageOptions: normalized });
            }

            if (isImageUpload) {
              // ✅ fixo: image/* e 5MB
              configJson = JSON.stringify({
                uploadConfig: { accept: "image/*", maxSizeMB: 5 },
              });
            }

            return {
              id:
                typeof f?.id === "number"
                  ? f.id
                  : String(f?.id || "").match(/^\d+$/)
                  ? Number(f.id)
                  : null,

              tempId: typeof f?.id === "string" ? f.id : null,

              label: String(f?.label || "").trim(),
              helperText: String(f?.helper || f?.helperText || "").trim() || null,
              fieldType,
              required: !!f?.required,
              sortOrder: f?.sortOrder ?? idx + 1,
              configJson,
              status: f?.status || "ACTIVE",
            };
          }),
      };

      const formData = buildSaveFormData({ payload, fields });

      // ✅ endpoint multipart
      const res = await formAPI.createNewFormMultipart(formData);
      const saved = res?.data || res;

      if (saved?.id) {
        setFormModel((p) => ({
          ...p,
          id: saved.id,
          active: saved.active ?? p.active,
          status: saved.status ?? (saved.active ? "ACTIVE" : "INACTIVE"),
        }));
      }

      // ✅ rehidrata fields do retorno (se vier)
      if (Array.isArray(saved?.fields)) {
        // revoga previews atuais antes de substituir state
        (fields || []).forEach((f) => revokeAllPreviewsFromField(f));

        const mapped = saved.fields.map((sf, index) => {
          const conf = safeJson(sf?.configJson);
          const fieldType = sf?.fieldType ?? "TEXT";

          return {
            id: sf?.id,
            label: sf?.label ?? "",
            type: fieldType,
            required: !!sf?.required,
            helper: sf?.helperText ?? "",
            sortOrder: sf?.sortOrder ?? index + 1,
            status: sf?.status ?? "ACTIVE",
            configJson: sf?.configJson ?? null,

            options: Array.isArray(conf?.options) ? conf.options : undefined,

            imageOptions:
              fieldType === "IMAGE_SELECT" && Array.isArray(conf?.imageOptions)
                ? conf.imageOptions.map((x) => ({
                    text: x?.text ?? "",
                    file: null,
                    previewUrl: "",
                    imageUrl: x?.imageUrl ?? "",
                  }))
                : undefined,
          };
        });

        mapped.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setFields(mapped);
      }

      HallAppToast.success("Formulário salvo!");
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  }, [fields, formModel]);

  const toggleActive = useCallback(async () => {
    try {
      if (!formModel?.id) {
        setFormModel((p) => {
          const active = !p.active;
          return { ...p, active, status: active ? "ACTIVE" : "INACTIVE" };
        });
        return;
      }

      setLoading(true);

      const companyId = companyAPI.getCompanyManagment().id;
      const newActive = !formModel.active;

      await formAPI.setActive(formModel.id, companyId, newActive);

      setFormModel((p) => ({
        ...p,
        active: newActive,
        status: newActive ? "ACTIVE" : "INACTIVE",
      }));

      HallAppToast.success(newActive ? "Formulário ativado!" : "Formulário inativado!");
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  }, [formModel]);

  const typeLabel = (type) => FIELD_TYPES.find((t) => t.value === type)?.label || type;

  const onDoneEditor = () => {
    if (isMobile()) setShowMobileEditor(false);
    else setEditingFieldId(null);
  };

  return (
    <div className="container fb-page">
      <ReportHeader name="Formulário" />

      <div className="fb-header">
        <div className="fb-header__title">Prontuário & Formulários</div>
        <div className="fb-header__subtitle">Customização de formulário</div>

        <div className="fb-header__row">
          <div className="fb-header__switch">
            <Form.Check
              type="switch"
              checked={!!formModel.active}
              onChange={toggleActive}
              label={formModel.active ? "Ativo" : "Inativo"}
              disabled={isLoading}
            />
          </div>

          <div className="fb-header__save">
            {isLoading ? <AppLoading /> : <AppButton name="Salvar" fullWidth={false} onClick={saveForm} />}
          </div>
        </div>
      </div>

      <div className="fb-content">
        <Row className="g-3">
          <Col xs={12} lg={editingField ? 7 : 12}>
            <div className="fb-card fb-forminfo">
              <Row className="g-2">
                <Col xs={12}>
                  <Form.Label className="fb-rb-label">Nome do formulário</Form.Label>
                  <Form.Control
                    className="fb-control"
                    value={formModel.name}
                    onChange={(e) => setFormModel((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Ex: Anamnese Capilar"
                    disabled={isLoading}
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label className="fb-rb-label">Descrição (opcional)</Form.Label>
                  <Form.Control
                    className="fb-control"
                    value={formModel.description}
                    onChange={(e) => setFormModel((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Ex: Formulário padrão de atendimento"
                    disabled={isLoading}
                  />
                </Col>
              </Row>

              <div className="fb-add-grid">
                {FIELD_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className="fb-add-tile"
                    onClick={() => addField(t.value)}
                    disabled={isLoading}
                  >
                    <div className="fb-add-tile__title">Adicionar</div>
                    <div className="fb-add-tile__sub">{t.label}</div>
                  </button>
                ))}
              </div>

              <div className="fb-tip">Toque em um campo para editar. Use os botões “Subir/Descer” para ordenar.</div>
            </div>

            <div className="fb-list">
              {fields
                .slice()
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((f, idx) => (
                  <div
                    key={f.id}
                    className={`fb-card fb-field ${editingFieldId === f.id ? "is-active" : ""}`}
                    role="button"
                    onClick={() => openEditor(f.id)}
                  >
                    <div className="fb-field__top">
                      <div className="fb-field__left">
                        <div className="fb-field__index">{idx + 1}</div>

                        <div className="fb-field__text">
                          <div className="fb-field__label">
                            {f.label || "—"}
                            {f.required ? <Badge className="fb-badge-req">Obrigatório</Badge> : null}
                          </div>

                          <div className="fb-field__meta">{typeLabel(f.type)}</div>

                          {f.helper ? <div className="fb-field__helper">{f.helper}</div> : null}
                        </div>
                      </div>
                    </div>

                    <div className="fb-field__actions" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="fb-action-btn" onClick={() => openEditor(f.id)}>
                        Editar
                      </button>

                      <button type="button" className="fb-action-btn" disabled={idx === 0} onClick={() => moveField(f.id, -1)}>
                        Subir
                      </button>

                      <button
                        type="button"
                        className="fb-action-btn"
                        disabled={idx === fields.length - 1}
                        onClick={() => moveField(f.id, +1)}
                      >
                        Descer
                      </button>

                      <button type="button" className="fb-action-btn" onClick={() => duplicateField(f.id)}>
                        Duplicar
                      </button>

                      <button
                        type="button"
                        className="fb-action-btn fb-action-btn--danger"
                        onClick={() => removeField(f.id)}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ))}

              {!fields.length && (
                <div className="fb-card fb-empty">
                  <div className="fb-empty__title">Nenhum campo adicionado</div>
                  <div className="fb-empty__sub">Comece adicionando um tipo de campo acima.</div>
                  <AppLinkButton name="Adicionar Texto curto" fullWidth={true} onClick={() => addField("TEXT")} />
                </div>
              )}
            </div>
          </Col>

          {editingField && (
            <Col xs={12} lg={5} className="d-none d-lg-block">
              <div className="fb-drawer">
                <div className="fb-drawer__head">
                  <div className="fb-drawer__title">Editar campo</div>
                  <button type="button" className="fb-action-btn" onClick={() => setEditingFieldId(null)}>
                    Fechar
                  </button>
                </div>

                <EditorContent
                  field={editingField}
                  updateField={updateField}
                  removeField={removeField}
                  onDone={onDoneEditor}
                />
              </div>
            </Col>
          )}
        </Row>
      </div>

      <div style={{ height: "calc(50px + 72px)" }} />

      <Modal show={showMobileEditor && !!editingField} onHide={closeEditor} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar campo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditorContent
            field={editingField}
            updateField={updateField}
            removeField={removeField}
            onDone={onDoneEditor}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
